// src/utils/sanitization.ts

import DOMPurify from 'dompurify';

/**
 * Configuración predeterminada para DOMPurify
 */
const DEFAULT_CONFIG = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'ul', 'ol', 'li', 'br', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id', 'style'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'object', 'embed', 'input', 'textarea'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onfocus', 'onblur'],
  ADD_ATTR: ['target'],
  FORCE_HTTPS: true,
  USE_PROFILES: { html: true }
};

/**
 * Sanitiza una cadena HTML y la guarda en localStorage
 * @param key - La clave para almacenar en localStorage
 * @param value - El valor HTML/texto a sanitizar
 * @param customConfig - Configuración opcional personalizada para DOMPurify
 */
export const sanitizeAndStore = (
  key: string,
  value: string,
  customConfig?: typeof DEFAULT_CONFIG
): void => {
  if (!key || typeof key !== 'string') {
    throw new Error('La clave debe ser una cadena válida');
  }

  // IMPORTANT: Don't sanitize tokens and sensitive data that are not HTML
  // JWTs and cryptographic tokens should NOT pass through DOMPurify
  const BYPASS_SANITIZATION_KEYS = ['token', 'userId', 'role', 'username'];

  let cleanValue: string;

  if (BYPASS_SANITIZATION_KEYS.includes(key)) {
    cleanValue = value; // Store as-is without sanitization
  } else {
    const config = customConfig || DEFAULT_CONFIG;
    cleanValue = DOMPurify.sanitize(value, config) as string;
  }

  try {
    localStorage.setItem(key, cleanValue);
  } catch (error) {
    console.error('Error al guardar en localStorage:', error);
    throw new Error('No se pudo guardar el valor sanitizado en localStorage');
  }
};

/**
 * Recupera y sanitiza un valor de localStorage
 * @param key - La clave para recuperar de localStorage
 * @param customConfig - Configuración opcional personalizada para DOMPurify
 */
export const getAndSanitize = (
  key: string,
  customConfig?: typeof DEFAULT_CONFIG
): string | null => {
  const value = localStorage.getItem(key);

  if (!value) return null;

  // IMPORTANT: Don't sanitize tokens and sensitive data that are not HTML
  // JWTs and cryptographic tokens should NOT pass through DOMPurify
  const BYPASS_SANITIZATION_KEYS = ['token', 'userId', 'role', 'username'];

  if (BYPASS_SANITIZATION_KEYS.includes(key)) {
    return value;
  }

  const config = customConfig || DEFAULT_CONFIG;
  return DOMPurify.sanitize(value, config) as string;
};

/**
 * Sanitiza datos antes de enviarlos a una API
 * @param data - Los datos a sanitizar (string u objeto)
 * @param customConfig - Configuración opcional personalizada para DOMPurify
 */
export const sanitizeForApi = <T extends Record<string, any> | string>(
  data: T, 
  customConfig?: typeof DEFAULT_CONFIG
): T => {
  const config = customConfig || DEFAULT_CONFIG;
  
  if (typeof data === 'string') {
    return DOMPurify.sanitize(data, config) as unknown as T;
  }
  
  if (data && typeof data === 'object') {
    const sanitizedData = { ...data } as Record<string, any>;
    
    Object.keys(sanitizedData).forEach(key => {
      if (typeof sanitizedData[key] === 'string') {
        sanitizedData[key] = DOMPurify.sanitize(sanitizedData[key], config) as string;
      }
    });
    
    return sanitizedData as T;
  }
  
  return data;
};

/**
 * Sanitiza HTML para renderizado seguro en componentes React
 * @param html - El HTML a sanitizar
 * @param customConfig - Configuración opcional personalizada para DOMPurify
 */
export const sanitizeHtml = (
  html: string, 
  customConfig?: typeof DEFAULT_CONFIG
): string => {
  const config = customConfig || DEFAULT_CONFIG;
  return DOMPurify.sanitize(html, config) as string;
};

/**
 * Verifica si un string contiene scripts potencialmente maliciosos
 * @param input - La cadena a verificar
 */
export const containsMaliciousCode = (input: string): boolean => {
  if (!input || typeof input !== 'string') return false;
  
  const sanitized = DOMPurify.sanitize(input) as string;
  return sanitized !== input;
};

export default {
  sanitizeAndStore,
  getAndSanitize,
  sanitizeForApi,
  sanitizeHtml,
  containsMaliciousCode,
  DEFAULT_CONFIG,
};